// Currency configuration for different countries
// Based on Stripe's supported currencies by country

export type CountryCode = 
    | "US" | "GB" | "CA" | "AU" | "NZ"
    | "FR" | "DE" | "IT" | "ES" | "NL" | "BE" | "AT" | "CH" | "SE" | "NO" | "DK" | "FI"
    | "JP" | "SG" | "HK" | "MY" | "TH" | "ID" | "PH"
    | "MX" | "BR"
    | "AE" | "SA"

export type CurrencyCode = string

interface CountryInfo {
    name: string
    defaultCurrency: CurrencyCode
    supportedCurrencies: CurrencyCode[]
    flag: string
}

// Country configurations with their supported currencies
export const COUNTRY_CONFIGS: Record<CountryCode, CountryInfo> = {
    // North America
    US: {
        name: "United States",
        defaultCurrency: "USD",
        supportedCurrencies: ["USD"],
        flag: "🇺🇸",
    },
    CA: {
        name: "Canada",
        defaultCurrency: "CAD",
        supportedCurrencies: ["CAD", "USD"],
        flag: "🇨🇦",
    },
    MX: {
        name: "Mexico",
        defaultCurrency: "MXN",
        supportedCurrencies: ["MXN", "USD"],
        flag: "🇲🇽",
    },
    
    // Europe
    GB: {
        name: "United Kingdom",
        defaultCurrency: "GBP",
        supportedCurrencies: ["GBP", "EUR", "USD"],
        flag: "🇬🇧",
    },
    FR: {
        name: "France",
        defaultCurrency: "EUR",
        supportedCurrencies: ["EUR", "USD"],
        flag: "🇫🇷",
    },
    DE: {
        name: "Germany",
        defaultCurrency: "EUR",
        supportedCurrencies: ["EUR", "USD"],
        flag: "🇩🇪",
    },
    IT: {
        name: "Italy",
        defaultCurrency: "EUR",
        supportedCurrencies: ["EUR", "USD"],
        flag: "🇮🇹",
    },
    ES: {
        name: "Spain",
        defaultCurrency: "EUR",
        supportedCurrencies: ["EUR", "USD"],
        flag: "🇪🇸",
    },
    NL: {
        name: "Netherlands",
        defaultCurrency: "EUR",
        supportedCurrencies: ["EUR", "USD"],
        flag: "🇳🇱",
    },
    BE: {
        name: "Belgium",
        defaultCurrency: "EUR",
        supportedCurrencies: ["EUR", "USD"],
        flag: "🇧🇪",
    },
    AT: {
        name: "Austria",
        defaultCurrency: "EUR",
        supportedCurrencies: ["EUR", "USD"],
        flag: "🇦🇹",
    },
    CH: {
        name: "Switzerland",
        defaultCurrency: "CHF",
        supportedCurrencies: ["CHF", "EUR", "USD"],
        flag: "🇨🇭",
    },
    SE: {
        name: "Sweden",
        defaultCurrency: "SEK",
        supportedCurrencies: ["SEK", "EUR", "USD"],
        flag: "🇸🇪",
    },
    NO: {
        name: "Norway",
        defaultCurrency: "NOK",
        supportedCurrencies: ["NOK", "EUR", "USD"],
        flag: "🇳🇴",
    },
    DK: {
        name: "Denmark",
        defaultCurrency: "DKK",
        supportedCurrencies: ["DKK", "EUR", "USD"],
        flag: "🇩🇰",
    },
    FI: {
        name: "Finland",
        defaultCurrency: "EUR",
        supportedCurrencies: ["EUR", "USD"],
        flag: "🇫🇮",
    },
    
    // Asia-Pacific
    JP: {
        name: "Japan",
        defaultCurrency: "JPY",
        supportedCurrencies: ["JPY", "USD"],
        flag: "🇯🇵",
    },
    SG: {
        name: "Singapore",
        defaultCurrency: "SGD",
        supportedCurrencies: ["SGD", "USD"],
        flag: "🇸🇬",
    },
    HK: {
        name: "Hong Kong",
        defaultCurrency: "HKD",
        supportedCurrencies: ["HKD", "USD"],
        flag: "🇭🇰",
    },
    AU: {
        name: "Australia",
        defaultCurrency: "AUD",
        supportedCurrencies: ["AUD", "USD"],
        flag: "🇦🇺",
    },
    NZ: {
        name: "New Zealand",
        defaultCurrency: "NZD",
        supportedCurrencies: ["NZD", "USD"],
        flag: "🇳🇿",
    },
    MY: {
        name: "Malaysia",
        defaultCurrency: "MYR",
        supportedCurrencies: ["MYR", "USD"],
        flag: "🇲🇾",
    },
    TH: {
        name: "Thailand",
        defaultCurrency: "THB",
        supportedCurrencies: ["THB", "USD"],
        flag: "🇹🇭",
    },
    ID: {
        name: "Indonesia",
        defaultCurrency: "IDR",
        supportedCurrencies: ["IDR", "USD"],
        flag: "🇮🇩",
    },
    PH: {
        name: "Philippines",
        defaultCurrency: "PHP",
        supportedCurrencies: ["PHP", "USD"],
        flag: "🇵🇭",
    },
    
    // South America
    BR: {
        name: "Brazil",
        defaultCurrency: "BRL",
        supportedCurrencies: ["BRL", "USD"],
        flag: "🇧🇷",
    },
    
    // Middle East
    AE: {
        name: "United Arab Emirates",
        defaultCurrency: "AED",
        supportedCurrencies: ["AED", "USD"],
        flag: "🇦🇪",
    },
    SA: {
        name: "Saudi Arabia",
        defaultCurrency: "SAR",
        supportedCurrencies: ["SAR", "USD"],
        flag: "🇸🇦",
    },
}

// Currency symbols and formatting
export const CURRENCY_SYMBOLS: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    CAD: "C$",
    AUD: "A$",
    NZD: "NZ$",
    JPY: "¥",
    CNY: "¥",
    HKD: "HK$",
    SGD: "S$",
    MYR: "RM",
    THB: "฿",
    IDR: "Rp",
    PHP: "₱",
    INR: "₹",
    KRW: "₩",
    MXN: "Mex$",
    BRL: "R$",
    AED: "د.إ",
    SAR: "﷼",
    CHF: "CHF",
    SEK: "kr",
    NOK: "kr",
    DKK: "kr",
}

// Helper functions
export function getCurrencySymbol(currency: string): string {
    return CURRENCY_SYMBOLS[currency] || currency
}

export function formatCurrency(amount: number, currency: string): string {
    const symbol = getCurrencySymbol(currency)
    
    // Handle zero-decimal currencies (like JPY, KRW)
    const zeroDecimalCurrencies = ["JPY", "KRW", "IDR"]
    const isZeroDecimal = zeroDecimalCurrencies.includes(currency)
    
    if (isZeroDecimal) {
        return `${symbol}${amount.toLocaleString()}`
    }
    
    return `${symbol}${(amount / 100).toFixed(2)}`
}

export function getCountryFromLocation(location?: any): CountryCode {
    // Try to detect country from user's location object
    // This could be enhanced with actual geolocation logic
    if (!location) return "US"
    
    // Check if location has country code
    if (location.country_code) {
        const code = location.country_code.toUpperCase()
        if (code in COUNTRY_CONFIGS) {
            return code as CountryCode
        }
    }
    
    // Default to US if we can't determine
    return "US"
}

export function getSupportedCountries() {
    return Object.entries(COUNTRY_CONFIGS).map(([code, config]) => ({
        code,
        ...config,
    }))
}